import {useCallback} from "react";
import {useTranslation} from "react-i18next";

import {ResponseError as WorkerResponseError} from "../../api/authority/generated/index.ts";
import {ResponseError as AuthorityResponseError} from "../../api/authority/generated/index.ts";

import ApiErrors from "../../../translations/api-errors.json";
import {isSessionAuthenticationError} from "./sessionAuthentication.js";

let findDefinedApiError = (apiMessage) => {
    return (Object.hasOwn(ApiErrors.singular, apiMessage) ? ApiErrors.singular[apiMessage] : null)
        ?? (Object.hasOwn(ApiErrors.plural, apiMessage) ? ApiErrors.plural[apiMessage] : null);
}

async function readErrorResponseBody(response) {
    try {
        return await response.clone().json();
    } catch {
        try {
            const text = await response.clone().text();

            if (text.trim() === "") {
                return null;
            }

            return {message: text};
        } catch {
            return null;
        }
    }
}

export function useErrorTranslation() {
    const {t} = useTranslation();

    const errorTranslationMethod = useCallback((error) => {
        return new Promise(
            (resolve, reject) => {

                if (error === null || error === undefined) {
                    resolve('');
                    return;
                }

                if ((typeof error) === 'string') {
                    resolve(error);
                    return;
                }

                if (isSessionAuthenticationError(error)) {
                    resolve('');
                    return;
                }

                if (error instanceof WorkerResponseError || error instanceof AuthorityResponseError) {
                    readErrorResponseBody(error.response)
                        .then((json) => {
                            const apiMessage = json?.message;

                            if (typeof apiMessage !== "string" || apiMessage.trim() === "") {
                                resolve('');
                                return;
                            }

                            let definedApiError = findDefinedApiError(apiMessage);
                            if (definedApiError === null) {
                                resolve(t('validation.unknown-error'));
                            } else {
                                resolve(t(definedApiError, json.context));
                            }
                        })
                        .catch(() => resolve(''));

                    return;
                }

                if (typeof error.message === 'string') {
                    let definedApiError = findDefinedApiError(error.message);
                    if (definedApiError === null) {
                        resolve(t('validation.unknown-error'));
                    } else {
                        resolve(t(definedApiError, error.context));
                    }
                    return;
                }

                resolve(t('validation.unknown-error'));
            }
        );
    }, [t]);

    return {
        et: errorTranslationMethod,
    };
}
